"use client";

/* eslint-disable indent */
import { FC, useContext, useEffect, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroller";
import { useQuery } from "react-query";
import { captureException } from "@sentry/react";
import { isEqual } from "lodash";
import { Button } from "components/atoms/Button";
import { ButtonType } from "components/atoms/Button/types";
import GalleryImage from "components/atoms/GalleryImage";
import { DeleteConfirmationModal } from "components/molecules/DeleteModal";
import EligibleCompetitionsModal from "components/molecules/EligibleCompetitionsModal";
import SearchAccordion from "components/molecules/SearchAccordion";
import ShareModal from "components/molecules/ShareModal";
import { deleteImage, getFilterFields, searchImages, voteOnImage } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, FilterFieldDTO, ImageDTO, ImageSearchQueryDTO } from "types";
import { Label, Span, SubTitle } from "utils/styling/typography.styled";
import * as Styled from "./CompetitionGallery.styled";
import Masonry from "react-masonry-css";
type CompetitionGalleryProps = {
  gallerySubtitle: string;
  userGallery: boolean;
  defaultFilters?: {
    [key: string]: string;
  };
  doAction: (imageId: string) => void;
  createdAtBefore?: string;
};
export const CompetitionGallery: FC<CompetitionGalleryProps> = ({
  gallerySubtitle,
  userGallery,
  defaultFilters,
  doAction,
  createdAtBefore
}) => {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [page, setPage] = useState(0);
  const [images, setImages] = useState<ImageDTO[]>([]);
  const [sort, setSort] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filterFields, setFilterFields] = useState<FilterFieldDTO[]>([]);
  const [filters, setFilters] = useState<any>({});
  const [query, setQuery] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const imageIdsSet = useRef(new Set<string>());
  const [imageId, setImageId] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [showEligibleCompetitionsModal, setShowEligibleCompetitionsModal] = useState(false);
  const [currentImageId, setCurrentImageId] = useState<string | null>(null);
  const prevSortRef = useRef(sort);
  const prevSortOrderRef = useRef(sortOrder);
  const prevFiltersRef = useRef(filters);
  const prevQueryRef = useRef(query);
  const handleCompetitionsClick = (imageId: string) => {
    setCurrentImageId(imageId);
    setShowEligibleCompetitionsModal(true);
  };
  const handleCloseEligibleCompetitionsModal = () => {
    setShowEligibleCompetitionsModal(false);
    setCurrentImageId(null);
    dispatch({
      type: AppReducerActionType.TOGGLE_OVERLAY_VISIBILITY,
      payload: {
        ...state,
        isOverlayVisible: false
      }
    });
  };
  const handleShareClick = (e: React.MouseEvent, imageId?: string) => {
    e.preventDefault();
    e.stopPropagation();
    const selectedImage = images.find(image => image.id === imageId);
    if (selectedImage) {
      setIsShareModalOpen(true);
    }
  };
  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
  };
  const prepareForDelete = (imageId: string) => {
    setImageToDelete(imageId);
    setShowDeleteConfirmation(true);
  };
  const handleConfirmDelete = async () => {
    if (!imageToDelete) return;
    try {
      await deleteImage(imageToDelete);
      const newImageDtos = images.filter(img => img.id !== imageToDelete);
      setImages(newImageDtos);
      setShowDeleteConfirmation(false);
      setImageToDelete(null);
    } catch (error: any) {
      captureException("Failed to delete image", error);
      setShowDeleteConfirmation(false);
      setImageToDelete(null);
    }
  };
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setImageToDelete(null);
  };
  const handleVote = async (imageId: string) => {
    if (!state.isLoggedIn) {
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    const currentImage = images.find(img => img.id === imageId);
    if (!currentImage) return;
    const newVoteStatus = !currentImage.isVotedByUser;
    try {
      await voteOnImage(imageId, newVoteStatus);
      setImages(prev => prev.map(img => img.id === imageId ? {
        ...img,
        isVotedByUser: newVoteStatus,
        voteCount: img.voteCount + (newVoteStatus ? 1 : -1)
      } : img));
    } catch (error: any) {
      captureException("Failed to update vote status.", error);
    }
  };
  useEffect(() => {
    const fetchFilters = async () => {
      const fields = await getFilterFields();
      setFilterFields(fields);
    };
    fetchFilters();
  }, []);
  useEffect(() => {
    if (defaultFilters) {
      setFilters(defaultFilters);
    }
  }, [defaultFilters]);
  const fetchImages = async ({
    page,
    sort,
    filters,
    query
  }: {
    page: number;
    sort: string;
    filters: any;
    query: string;
  }) => {
    try {
      const searchQuery: ImageSearchQueryDTO = {
        query,
        sortField: sort,
        sortOrder: sortOrder,
        filters,
        page,
        pageSize: 15,
        userGallery: userGallery,
        createdAfter: createdAtBefore ? new Date(createdAtBefore).toISOString() : null
      };
      const response = await searchImages(searchQuery);
      if (response.error) {
        setError(typeof response.error === "string" ? response.error : response.error.message || "Unknown error");
        setHasMore(false);
        return {
          error: response.error
        };
      }
      if (response.image && response.image.length === 0) {
        setHasMore(false);
      }
      return response;
    } catch (e) {
      captureException(e);
      setError("Failed to get images");
      setHasMore(false);
      return {
        error: "Failed to get images"
      };
    }
  };
  const {
    refetch
  } = useQuery(["images", page, sort, sortOrder, filters, query], () => fetchImages({
    page,
    sort,
    filters,
    query
  }), {
    keepPreviousData: true,
    onSuccess: newData => {
      if (newData.image && Array.isArray(newData.image) && newData.image.length > 0) {
        const uniqueImages = newData.image.filter((image: ImageDTO) => !imageIdsSet.current.has(image.id));
        uniqueImages.forEach((image: ImageDTO) => imageIdsSet.current.add(image.id));
        setImages(prev => [...prev, ...uniqueImages]);
        setHasMore(uniqueImages.length > 0);
      } else {
        setHasMore(false);
      }
      setLoadingMore(false);
    },
    onError: () => {
      setHasMore(false);
    }
  });
  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = event.target as HTMLInputElement | HTMLSelectElement;
    const checked = type === "checkbox" ? (event.target as HTMLInputElement).checked : false;
    setFilters((prevFilters: any) => ({
      ...prevFilters,
      [name]: type === "checkbox" ? checked : value
    }));
  };
  const loadMore = () => {
    if (hasMore && !loadingMore) {
      setLoadingMore(true);
      setPage(prevPage => prevPage + 1);
    }
  };
  useEffect(() => {
    const prevSort = prevSortRef.current;
    const prevSortOrder = prevSortOrderRef.current;
    const prevFilters = prevFiltersRef.current;
    const prevQuery = prevQueryRef.current;

    // Logic to determine if any values have changed
    const hasChanged = prevSort !== sort || prevSortOrder !== sortOrder || !isEqual(prevFilters, filters) || prevQuery !== query;
    if (hasChanged) {
      setPage(0);
      setImages([]);
      setHasMore(true);
      imageIdsSet.current.clear();
    }

    // Update previous values for next comparison
    prevSortRef.current = sort;
    prevSortOrderRef.current = sortOrder;
    prevFiltersRef.current = filters;
    prevQueryRef.current = query;
  }, [sort, sortOrder, filters, query]);
  useEffect(() => {
    if (page >= 0 && loadingMore) {
      refetch().then(() => {
        setLoadingMore(false); // Reset only after data fetching completes
      });
    }
  }, [page, refetch, loadingMore]);
  const handleSelectImage = (imageId: string) => {
    setImageId(imageId);
    doAction(imageId);
  };
  const noOp = () => {};
  const isUserImage = (imageUsername: string) => imageUsername === "user";
  const isSelected = (selectedImageId: string) => selectedImageId === imageId;
  const breakpointColumnsObj = {
    default: 6,
    1280: 4,
    1024: 3,
    768: 2,
    576: 1
  };
  const renderImage = (image: ImageDTO) => <GalleryImage key={image.id} item={image} onImageClick={() => handleSelectImage(image.id)} isUserImage={isUserImage} handleToggle={noOp} handleEditClick={noOp} handleADetailerClick={noOp} handleFaceSwapClick={noOp} handleBGRemovalClick={noOp} handleInpaintClick={noOp} handleUpscaleClick={noOp} prepareForDelete={prepareForDelete} handleVote={handleVote} isSelectionMode={false} handleSelectImage={handleSelectImage} isSelected={isSelected(image.id)} userGallery={userGallery} handleShareClick={handleShareClick} showIcons={false} showDeleteConfirmation={showDeleteConfirmation} handleCompetitionsClick={() => handleCompetitionsClick(image.id)} data-sentry-element="GalleryImage" data-sentry-component="renderImage" data-sentry-source-file="index.tsx" />;
  return <Styled.ImageGalleryWrapper data-sentry-element="unknown" data-sentry-component="CompetitionGallery" data-sentry-source-file="index.tsx">
            <SubTitle data-sentry-element="SubTitle" data-sentry-source-file="index.tsx">{gallerySubtitle}</SubTitle>
            <SearchAccordion filterFields={filterFields} filters={filters} handleFilterChange={handleFilterChange} query={query} setQuery={setQuery} sort={sort} setSort={setSort} sortOrder={sortOrder} setSortOrder={setSortOrder} disabledFilters={Object.keys(defaultFilters || {})} // Disable criteria fields
    data-sentry-element="SearchAccordion" data-sentry-source-file="index.tsx" />
            {error && <div>{error}</div>}
            {!hasMore && images.length === 0 && <Label>No images to display</Label>}

            <InfiniteScroll pageStart={0} loadMore={loadMore} hasMore={hasMore} loader={<div key={0}>Loading ...</div>} data-sentry-element="InfiniteScroll" data-sentry-source-file="index.tsx">
                {images.length > 0 && <Masonry breakpointCols={breakpointColumnsObj} className="flex w-auto -ml-4" columnClassName="pl-4 bg-clip-padding">
                        {images.map(renderImage)}
                    </Masonry>}
            </InfiniteScroll>
            {!hasMore && <Button type="submit" variant={ButtonType.PRIMARY} disabled={false} onClick={() => {
      setHasMore(true);
      loadMore();
    }}>
                    <Span>Load More</Span>
                </Button>}
            <EligibleCompetitionsModal isOpen={showEligibleCompetitionsModal} onCancel={handleCloseEligibleCompetitionsModal} imageId={currentImageId} data-sentry-element="EligibleCompetitionsModal" data-sentry-source-file="index.tsx" />
            <DeleteConfirmationModal isOpen={showDeleteConfirmation} onConfirm={handleConfirmDelete} onCancel={handleCancelDelete} text="image" data-sentry-element="DeleteConfirmationModal" data-sentry-source-file="index.tsx" />
            <ShareModal isOpen={isShareModalOpen} image={images.find(img => img.id === imageId) || null} onClose={handleCloseShareModal} data-sentry-element="ShareModal" data-sentry-source-file="index.tsx" />
        </Styled.ImageGalleryWrapper>;
};
export default CompetitionGallery;